import { useAuth } from '../contexts/AuthContext';
import { useOrgSettings } from '../contexts/OrgSettingsContext';

/**
 * Client-side mirror of the server's accessScope.isBillingManager().
 *
 * True for the roles allowed into the firm-wide-money half of the app:
 * Retainers, Reconciliation, Receivables, TDS Tracker, Credit Notes, the
 * monthly wizard, payment recording and the revenue reports.
 *
 * Admin and partners always; HR unless Settings → Roles → "HR Can Access
 * Billing" is switched off. Plain staff are never billing managers — they get
 * the Invoices tab alone, scoped by the server to invoices they raised.
 *
 * This only decides what to *render*. Every one of those endpoints enforces
 * the same rule server-side via requireBillingAccess, so a stale or patched
 * client gains nothing.
 */
export function useCanSeeBilling(): boolean {
  const { isAdmin, isHR, isPartner } = useAuth();
  const { settings } = useOrgSettings();
  if (isAdmin || isPartner) return true;
  if (isHR) return settings.roles.hrCanAccessBilling !== false;
  return false;
}

/**
 * Client-side mirror of the server's requirePartnerHROrAdmin middleware.
 *
 * Deliberately NOT the same as useCanSeeBilling(): HR is included
 * unconditionally here, where billing access lets HR be switched off via
 * Settings → Roles. Used for the sales register download, which is a
 * firm-level accounting extract rather than a collections tool.
 *
 * Render-only, as above — the endpoint enforces the same rule server-side.
 */
export function useIsPartnerHROrAdmin(): boolean {
  const { isAdmin, isHR, isPartner } = useAuth();
  return isAdmin || isHR || isPartner;
}
